import { useState, useRef, useEffect } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { useFinanceRecords, type FinanceRecord } from '../../store/financeStore';
import { useCustomers } from '../../store/customerStore';

interface Record {
  id: number;
  date: string;
  type: '收购' | '销售';
  variety: string;
  quantity: number;
  unit: string;
  price: number;
  amount: number;
  source: string;
  settlement: string;
  contact?: string;
  phone?: string;
  idCard?: string;
  qualityGrade?: string;
  moistureContent?: string;
  impurityRate?: string;
  remark?: string;
}

type SortKey = 'date' | 'quantity' | 'amount';
type SortDir = 'asc' | 'desc';

function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap z-50 pointer-events-none">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
}

function ExportDropdown({ onExport }: { onExport: (fmt: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formats = [
    { key: 'excel', label: '导出 Excel', icon: 'ri-file-excel-2-line', color: 'text-emerald-600' },
    { key: 'csv', label: '导出 CSV', icon: 'ri-file-text-line', color: 'text-blue-600' },
    { key: 'pdf', label: '导出 PDF', icon: 'ri-file-pdf-2-line', color: 'text-rose-600' },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
      >
        <i className="ri-download-2-line"></i>
        导出数据
        <i className={`ri-arrow-down-s-line transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-100 rounded-xl shadow-lg z-30 overflow-hidden">
          {formats.map(f => (
            <button
              key={f.key}
              onClick={() => {
                onExport(f.key);
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className={`${f.icon} text-base ${f.color}`}></i>
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function Ledger() {
  const { records: financeRecords } = useFinanceRecords();
  const { customers } = useCustomers();
  
  // 将 FinanceRecord 映射为台账 Record 结构
  const allRecords: Record[] = financeRecords.map(fr => {
    // 查找对应的客户信息
    const customer = customers.find(c => c.name === fr.source);
    
    return {
      id: fr.id,
      date: fr.date,
      type: fr.type,
      variety: fr.variety,
      quantity: fr.quantity,
      unit: fr.unit || '吨',
      price: fr.unitPrice,
      amount: fr.amount,
      source: fr.source,
      settlement: fr.settlementMethod,
      contact: fr.contact || customer?.contact || '',
      phone: fr.phone || customer?.phone || '',
      idCard: fr.idCard || customer?.idCard || '',
      qualityGrade: fr.qualityGrade || '',
      moistureContent: fr.moistureContent || '',
      impurityRate: fr.impurityRate || '',
      remark: fr.remark || '',
    };
  });

  const [filterOpen, setFilterOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchParams, setSearchParams] = useState({
    startDate: '',
    endDate: '',
    variety: '',
    type: '',
    keyword: '',
    customerName: '',
    amountMin: '',
    amountMax: '',
    settlement: '',
    varieties: [] as string[],
  });
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailRecord, setDetailRecord] = useState<Record | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const quarterAgo = new Date(Date.now() - 90 * 86400000).toISOString().split('T')[0];

  const setQuickDate = (type: 'today' | 'week' | 'month' | 'quarter') => {
    if (type === 'today') setSearchParams(p => ({ ...p, startDate: today, endDate: today }));
    if (type === 'week') setSearchParams(p => ({ ...p, startDate: weekAgo, endDate: today }));
    if (type === 'month') setSearchParams(p => ({ ...p, startDate: monthAgo, endDate: today }));
    if (type === 'quarter') setSearchParams(p => ({ ...p, startDate: quarterAgo, endDate: today }));
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = allRecords
    .filter(r => {
      if (searchParams.startDate && r.date < searchParams.startDate) return false;
      if (searchParams.endDate && r.date > searchParams.endDate) return false;
      if (searchParams.variety && r.variety !== searchParams.variety) return false;
      if (searchParams.varieties.length > 0 && !searchParams.varieties.includes(r.variety)) return false;
      if (searchParams.type && r.type !== searchParams.type) return false;
      if (searchParams.keyword && !r.source.includes(searchParams.keyword) && !r.variety.includes(searchParams.keyword)) return false;
      if (searchParams.customerName && r.source !== searchParams.customerName) return false;
      if (searchParams.settlement && r.settlement !== searchParams.settlement) return false;
      if (searchParams.amountMin && r.amount < Number(searchParams.amountMin)) return false;
      if (searchParams.amountMax && r.amount > Number(searchParams.amountMax)) return false;
      return true;
    })
    .sort((a, b) => {
      const v = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'date') return a.date > b.date ? v : -v;
      if (sortKey === 'quantity') return (a.quantity - b.quantity) * v;
      if (sortKey === 'amount') return (a.amount - b.amount) * v;
      return 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const totalPurchase = filtered
    .filter(r => r.type === '收购')
    .reduce((s, r) => s + r.amount, 0);
  const totalSale = filtered
    .filter(r => r.type === '销售')
    .reduce((s, r) => s + r.amount, 0);

  const SortIcon = ({ k }: { k: SortKey }) => (
    <i
      className={`ml-1 text-xs ${
        sortKey === k
          ? sortDir === 'asc'
            ? 'ri-sort-asc text-teal-500'
            : 'ri-sort-desc text-teal-500'
          : 'ri-expand-up-down-line text-gray-300'
      }`}
    ></i>
  );

  const handleExport = (fmt: string) => {
    alert(`正在导出 ${fmt.toUpperCase()} 格式，共 ${filtered.length} 条记录`);
  };

  const handleReset = () => {
    setSearchParams({ startDate: '', endDate: '', variety: '', type: '', keyword: '', customerName: '', amountMin: '', amountMax: '', settlement: '', varieties: [] });
    setCustomerSearch('');
    setPage(1);
  };

  // 获取所有品种列表（去重）
  const allVarieties = Array.from(new Set(allRecords.map(r => r.variety))).filter(Boolean);

  // 获取所有客户名称（去重）
  const allCustomerNames = Array.from(new Set([
    ...allRecords.map(r => r.source),
    ...customers.map(c => c.name),
  ])).filter(Boolean);

  const filteredCustomerNames = customerSearch
    ? allCustomerNames.filter(n => n.includes(customerSearch))
    : allCustomerNames;

  // 计算活跃筛选条件数量
  const activeFilterCount = [
    searchParams.startDate || searchParams.endDate,
    searchParams.variety,
    searchParams.varieties.length > 0,
    searchParams.type,
    searchParams.keyword,
    searchParams.customerName,
    searchParams.settlement,
    searchParams.amountMin || searchParams.amountMax,
  ].filter(Boolean).length;

  // 活跃筛选标签
  const activeFilterTags: { label: string; key: string }[] = [];
  if (searchParams.startDate || searchParams.endDate) {
    activeFilterTags.push({ label: `日期: ${searchParams.startDate || '不限'} ~ ${searchParams.endDate || '不限'}`, key: 'date' });
  }
  if (searchParams.variety) {
    activeFilterTags.push({ label: `品种: ${searchParams.variety}`, key: 'variety' });
  }
  if (searchParams.varieties.length > 0) {
    activeFilterTags.push({ label: `品种: ${searchParams.varieties.join('、')}`, key: 'varieties' });
  }
  if (searchParams.type) {
    activeFilterTags.push({ label: `类型: ${searchParams.type}`, key: 'type' });
  }
  if (searchParams.customerName) {
    activeFilterTags.push({ label: `客户: ${searchParams.customerName}`, key: 'customerName' });
  }
  if (searchParams.settlement) {
    activeFilterTags.push({ label: `结算: ${searchParams.settlement}`, key: 'settlement' });
  }
  if (searchParams.amountMin || searchParams.amountMax) {
    activeFilterTags.push({ label: `金额: ${searchParams.amountMin || '0'} ~ ${searchParams.amountMax || '不限'}`, key: 'amount' });
  }
  if (searchParams.keyword) {
    activeFilterTags.push({ label: `关键词: ${searchParams.keyword}`, key: 'keyword' });
  }

  const removeFilterTag = (key: string) => {
    setSearchParams(p => {
      const next = { ...p };
      if (key === 'date') { next.startDate = ''; next.endDate = ''; }
      if (key === 'variety') next.variety = '';
      if (key === 'varieties') next.varieties = [];
      if (key === 'type') next.type = '';
      if (key === 'customerName') next.customerName = '';
      if (key === 'settlement') next.settlement = '';
      if (key === 'amount') { next.amountMin = ''; next.amountMax = ''; }
      if (key === 'keyword') next.keyword = '';
      return next;
    });
    setPage(1);
  };

  const toggleVariety = (v: string) => {
    setSearchParams(p => {
      const list = p.varieties.includes(v)
        ? p.varieties.filter(x => x !== v)
        : [...p.varieties, v];
      return { ...p, varieties: list, variety: '' };
    });
    setPage(1);
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      {/* 详情弹窗 */}
      {detailRecord && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setDetailRecord(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">台账记录详情</h3>
              <button
                onClick={() => setDetailRecord(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="space-y-4">
              {/* 基本信息 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">基本信息</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['业务日期', detailRecord.date],
                    ['业务类型', detailRecord.type],
                    ['粮食品种', detailRecord.variety],
                    ['数量', `${detailRecord.quantity} ${detailRecord.unit}`],
                    ['单价', `¥${detailRecord.price.toLocaleString()}/吨`],
                    ['金额', `¥${detailRecord.amount.toLocaleString()}`],
                    ['结算方式', detailRecord.settlement],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <span className="text-xs text-gray-400 font-medium mb-1">{k}</span>
                      <span className="text-sm text-gray-800 font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 客户信息 */}
              <div className="bg-teal-50 rounded-lg p-4">
                <h4 className="text-xs font-bold text-teal-600 uppercase tracking-wide mb-3">
                  {detailRecord.type === '收购' ? '供应商信息' : '客户信息'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['企业/个人名称', detailRecord.source],
                    ['联系人', detailRecord.contact || '-'],
                    ['联系电话', detailRecord.phone || '-'],
                    ['身份证号', detailRecord.idCard || '-'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <span className="text-xs text-teal-600 font-medium mb-1">{k}</span>
                      <span className="text-sm text-gray-800 font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 质量指标 */}
              {(detailRecord.qualityGrade || detailRecord.moistureContent || detailRecord.impurityRate) && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-3">质量指标</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      ['质量等级', detailRecord.qualityGrade || '-'],
                      ['水分含量', detailRecord.moistureContent ? `${detailRecord.moistureContent}%` : '-'],
                      ['杂质率', detailRecord.impurityRate ? `${detailRecord.impurityRate}%` : '-'],
                    ].map(([k, v]) => (
                      <div key={k} className="flex flex-col">
                        <span className="text-xs text-orange-600 font-medium mb-1">{k}</span>
                        <span className="text-sm text-gray-800 font-semibold">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 备注信息 */}
              {detailRecord.remark && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">备注信息</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{detailRecord.remark}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 ml-64">
        {/* 顶部标题 */}
        <div className="bg-white border-b border-gray-100 px-8 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-xl font-bold text-gray-900">台账查询</h1>
            <p className="text-xs text-gray-400 mt-0.5">查询和管理粮食经营台账记录</p>
          </div>
          <ExportDropdown onExport={handleExport} />
        </div>

        <div className="p-8 space-y-5">
          {/* 汇总统计条 */}
          <div className="grid grid-cols-3 gap-4">
            {[
              {
                label: '查询结果总条数',
                value: filtered.length,
                unit: '条',
                icon: 'ri-list-check-2',
                color: 'text-gray-700',
                bg: 'bg-gray-50',
              },
              {
                label: '收购总金额',
                value: `¥${(totalPurchase / 10000).toFixed(1)}万`,
                unit: '',
                icon: 'ri-arrow-down-circle-line',
                color: 'text-teal-600',
                bg: 'bg-teal-50',
              },
              {
                label: '销售总金额',
                value: `¥${(totalSale / 10000).toFixed(1)}万`,
                unit: '',
                icon: 'ri-arrow-up-circle-line',
                color: 'text-orange-500',
                bg: 'bg-orange-50',
              },
            ].map(item => (
              <div
                key={item.label}
                className={`${item.bg} rounded-xl px-5 py-4 flex items-center gap-4`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-white shadow-sm ${item.color}`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-medium">{item.label}</div>
                  <div className={`text-xl font-bold ${item.color}`}>
                    {item.value}
                    <span className="text-sm font-normal ml-1">{item.unit}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 筛选面板（可折叠） */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div
              className="flex items-center justify-between px-6 py-4 cursor-pointer select-none hover:bg-gray-50 transition-colors"
              onClick={() => setFilterOpen(v => !v)}
            >
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                  <i className="ri-filter-3-line text-base"></i>
                </span>
                <span className="text-sm font-semibold text-gray-800">筛选条件</span>
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full font-medium">
                    {activeFilterCount} 项筛选
                  </span>
                )}
              </div>
              <i
                className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform duration-200 ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </div>

            {filterOpen && (
              <div className="px-6 pb-6 border-t border-gray-50">
                {/* 活跃筛选标签 */}
                {activeFilterTags.length > 0 && (
                  <div className="flex items-center flex-wrap gap-2 mt-4 mb-3">
                    <span className="text-xs text-gray-400 font-medium mr-1">当前筛选：</span>
                    {activeFilterTags.map(tag => (
                      <span key={tag.key} className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 text-xs rounded-full font-medium">
                        {tag.label}
                        <button onClick={(e) => { e.stopPropagation(); removeFilterTag(tag.key); }} className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-teal-200 transition-colors cursor-pointer ml-0.5">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </span>
                    ))}
                    <button onClick={handleReset} className="text-xs text-gray-400 hover:text-rose-500 transition-colors cursor-pointer ml-1">
                      清除全部
                    </button>
                  </div>
                )}

                {/* 快捷时间按钮 */}
                <div className="flex items-center gap-2 mt-4 mb-4">
                  <span className="text-xs text-gray-400 font-medium mr-1">快捷选择：</span>
                  {[
                    { label: '今日', action: () => setQuickDate('today') },
                    { label: '近7天', action: () => setQuickDate('week') },
                    { label: '近30天', action: () => setQuickDate('month') },
                    { label: '近90天', action: () => setQuickDate('quarter') },
                  ].map(btn => (
                    <button
                      key={btn.label}
                      onClick={btn.action}
                      className="px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                {/* 基础筛选行 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                      开始日期
                    </label>
                    <input
                      type="date"
                      value={searchParams.startDate}
                      onChange={e => {
                        setSearchParams(p => ({ ...p, startDate: e.target.value }));
                        setPage(1);
                      }}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                      结束日期
                    </label>
                    <input
                      type="date"
                      value={searchParams.endDate}
                      onChange={e => {
                        setSearchParams(p => ({ ...p, endDate: e.target.value }));
                        setPage(1);
                      }}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                      粮食品种
                    </label>
                    <select
                      value={searchParams.variety}
                      onChange={e => {
                        setSearchParams(p => ({ ...p, variety: e.target.value, varieties: [] }));
                        setPage(1);
                      }}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white cursor-pointer"
                    >
                      <option value="">全部品种</option>
                      {allVarieties.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                      业务类型
                    </label>
                    <select
                      value={searchParams.type}
                      onChange={e => {
                        setSearchParams(p => ({ ...p, type: e.target.value }));
                        setPage(1);
                      }}
                      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white cursor-pointer"
                    >
                      <option value="">全部类型</option>
                      <option value="收购">收购</option>
                      <option value="销售">销售</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                      关键词搜索
                    </label>
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
                      <input
                        type="text"
                        value={searchParams.keyword}
                        onChange={e => {
                          setSearchParams(p => ({ ...p, keyword: e.target.value }));
                          setPage(1);
                        }}
                        placeholder="客户/品种名称"
                        className="w-full pl-9 pr-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* 高级筛选展开按钮 */}
                <div className="mt-4">
                  <button
                    onClick={() => setAdvancedOpen(v => !v)}
                    className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                  >
                    <i className={`ri-equalizer-line text-sm`}></i>
                    高级筛选
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${advancedOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                </div>

                {/* 高级筛选面板 */}
                {advancedOpen && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* 客户名称选择器 */}
                      <div ref={customerDropdownRef} className="relative">
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                          客户/供应商名称
                        </label>
                        <div
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm bg-white flex items-center justify-between cursor-pointer hover:border-teal-400 transition-colors"
                          onClick={() => setShowCustomerDropdown(v => !v)}
                        >
                          <span className={searchParams.customerName ? 'text-gray-800' : 'text-gray-400'}>
                            {searchParams.customerName || '选择客户名称'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showCustomerDropdown ? 'rotate-180' : ''}`}></i>
                        </div>
                        {showCustomerDropdown && (
                          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 max-h-64 overflow-hidden">
                            <div className="p-2 border-b border-gray-100">
                              <div className="relative">
                                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                                <input
                                  type="text"
                                  value={customerSearch}
                                  onChange={e => setCustomerSearch(e.target.value)}
                                  placeholder="搜索客户名称..."
                                  className="w-full pl-8 pr-3 py-2 border border-gray-100 rounded-lg text-xs focus:ring-2 focus:ring-teal-400 focus:border-transparent bg-gray-50"
                                  onClick={e => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              <button
                                onClick={() => { setSearchParams(p => ({ ...p, customerName: '' })); setShowCustomerDropdown(false); setPage(1); }}
                                className={`w-full text-left px-4 py-2.5 text-xs hover:bg-teal-50 transition-colors cursor-pointer ${!searchParams.customerName ? 'text-teal-600 font-semibold bg-teal-50/50' : 'text-gray-700'}`}
                              >
                                全部客户
                              </button>
                              {filteredCustomerNames.map(name => (
                                <button
                                  key={name}
                                  onClick={() => { setSearchParams(p => ({ ...p, customerName: name })); setShowCustomerDropdown(false); setPage(1); }}
                                  className={`w-full text-left px-4 py-2.5 text-xs hover:bg-teal-50 transition-colors cursor-pointer flex items-center justify-between ${searchParams.customerName === name ? 'text-teal-600 font-semibold bg-teal-50/50' : 'text-gray-700'}`}
                                >
                                  <span>{name}</span>
                                  {searchParams.customerName === name && <i className="ri-check-line text-teal-500"></i>}
                                </button>
                              ))}
                              {filteredCustomerNames.length === 0 && (
                                <div className="px-4 py-4 text-xs text-gray-300 text-center">无匹配客户</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 结算方式 */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                          结算方式
                        </label>
                        <select
                          value={searchParams.settlement}
                          onChange={e => { setSearchParams(p => ({ ...p, settlement: e.target.value })); setPage(1); }}
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white cursor-pointer"
                        >
                          <option value="">全部结算方式</option>
                          {['现金', '转账', '承兑', '赊账', '支票'].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      {/* 金额范围 */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">
                          金额范围（元）
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={searchParams.amountMin}
                            onChange={e => { setSearchParams(p => ({ ...p, amountMin: e.target.value })); setPage(1); }}
                            placeholder="最小金额"
                            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white"
                          />
                          <span className="text-gray-300 text-xs">~</span>
                          <input
                            type="number"
                            value={searchParams.amountMax}
                            onChange={e => { setSearchParams(p => ({ ...p, amountMax: e.target.value })); setPage(1); }}
                            placeholder="最大金额"
                            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* 品种多选 */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">
                        品种多选（可同时选择多个品种）
                      </label>
                      <div className="flex items-center flex-wrap gap-2">
                        {allVarieties.map(v => {
                          const isSelected = searchParams.varieties.includes(v);
                          return (
                            <button
                              key={v}
                              onClick={() => toggleVariety(v)}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                                isSelected
                                  ? 'bg-teal-50 border-teal-400 text-teal-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                              }`}
                            >
                              <i className={`text-sm ${isSelected ? 'ri-checkbox-circle-fill text-teal-500' : 'ri-checkbox-blank-circle-line text-gray-300'}`}></i>
                              {v}
                            </button>
                          );
                        })}
                        {searchParams.varieties.length > 0 && (
                          <button
                            onClick={() => { setSearchParams(p => ({ ...p, varieties: [] })); setPage(1); }}
                            className="text-xs text-gray-400 hover:text-rose-500 transition-colors cursor-pointer ml-1"
                          >
                            清除选择
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-4 pt-4 border-t border-gray-50">
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium cursor-pointer transition-colors"
                  >
                    <i className="ri-refresh-line"></i>
                    重置条件
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 数据表格 */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-800">台账记录</span>
                <span className="px-2.5 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium">
                  共 {filtered.length} 条
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>每页</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white cursor-pointer"
                >
                  {PAGE_SIZE_OPTIONS.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <span>条</span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {paginated.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-300">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-50 mb-4">
                    <i className="ri-file-search-line text-4xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-400">暂无符合条件的台账记录</p>
                  <p className="text-xs text-gray-300 mt-1">请尝试调整筛选条件或前往快速录入添加数据</p>
                  <button
                    onClick={handleReset}
                    className="mt-4 px-4 py-2 text-xs text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    清除筛选条件
                  </button>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/80">
                      <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        <button
                          onClick={() => handleSort('date')}
                          className="flex items-center cursor-pointer hover:text-gray-600 transition-colors whitespace-nowrap"
                        >
                          日期 <SortIcon k="date" />
                        </button>
                      </th>
                      <th className="text-center py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        类型
                      </th>
                      <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        品种
                      </th>
                      <th className="text-right py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        <button
                          onClick={() => handleSort('quantity')}
                          className="flex items-center ml-auto cursor-pointer hover:text-gray-600 transition-colors whitespace-nowrap"
                        >
                          数量 <SortIcon k="quantity" />
                        </button>
                      </th>
                      <th className="text-right py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        单价(元)
                      </th>
                      <th className="text-right py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        <button
                          onClick={() => handleSort('amount')}
                          className="flex items-center ml-auto cursor-pointer hover:text-gray-600 transition-colors whitespace-nowrap"
                        >
                          金额(元) <SortIcon k="amount" />
                        </button>
                      </th>
                      <th className="text-left py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        客户/来源
                      </th>
                      <th className="text-center py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        结算
                      </th>
                      <th className="text-center py-3.5 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((record, idx) => (
                      <tr
                        key={record.id}
                        className={`border-b border-gray-50 hover:bg-teal-50/30 transition-colors ${
                          idx % 2 === 1 ? 'bg-gray-50/40' : 'bg-white'
                        }`}
                      >
                        <td className="py-3.5 px-5 text-sm text-gray-500 whitespace-nowrap">
                          {record.date}
                        </td>
                        <td className="py-3.5 px-5 text-center">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                              record.type === '收购'
                                ? 'bg-teal-50 text-teal-600'
                                : 'bg-orange-50 text-orange-500'
                            }`}
                          >
                            <i
                              className={`text-xs ${
                                record.type === '收购' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'
                              }`}
                            ></i>
                            {record.type}
                          </span>
                        </td>
                        <td className="py-3.5 px-5 text-sm font-semibold text-gray-800 whitespace-nowrap">
                          {record.variety}
                        </td>
                        <td className="py-3.5 px-5 text-sm text-right font-medium text-gray-700 whitespace-nowrap">
                          {record.quantity.toLocaleString()} {record.unit}
                        </td>
                        <td className="py-3.5 px-5 text-sm text-right text-gray-600 whitespace-nowrap">
                          {record.price.toLocaleString()}
                        </td>
                        <td className="py-3.5 px-5 text-sm text-right font-bold text-gray-800 whitespace-nowrap">
                          ¥{record.amount.toLocaleString()}
                        </td>
                        <td className="py-3.5 px-5 text-sm text-gray-700 whitespace-nowrap">
                          {record.source}
                        </td>
                        <td className="py-3.5 px-5 text-center">
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md whitespace-nowrap">
                            {record.settlement}
                          </span>
                        </td>
                        <td className="py-3.5 px-5">
                          <div className="flex items-center justify-center gap-1">
                            <Tooltip text="查看详情">
                              <button
                                onClick={() => setDetailRecord(record)}
                                className="w-7 h-7 flex items-center justify-center text-teal-500 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                              >
                                <i className="ri-eye-line text-sm"></i>
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* 分页 */}
            {filtered.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-between">
                <div className="text-xs text-gray-400">
                  显示第{' '}
                  {Math.min((page - 1) * pageSize + 1, filtered.length)}–
                  {Math.min(page * pageSize, filtered.length)} 条，共 {filtered.length} 条
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce<(number | '...')[]>((acc, p, i, arr) => {
                      if (i > 0 && typeof arr[i - 1] === 'number' && (p as number) - (arr[i - 1] as number) > 1)
                        acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) =>
                      p === '...' ? (
                        <span
                          key={`ellipsis-${i}`}
                          className="w-8 h-8 flex items-center justify-center text-gray-300 text-xs"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                            page === p
                              ? 'bg-teal-600 text-white shadow-sm'
                              : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 flex items-center justify-center border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
